import DOMPurify from 'dompurify';

/**
 * Sanitize text input removing ALL HTML tags
 * Use for: names, emails, short text fields
 * 
 * @param {string} text - Input text to sanitize
 * @param {number} maxLength - Maximum allowed length (default 1000)
 * @returns {string} Sanitized text with no HTML
 */
export const sanitizeText = (text, maxLength = 1000) => {
    if (!text || typeof text !== 'string') return '';

    // Remove all HTML tags
    const sanitized = DOMPurify.sanitize(text, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: []
    });

    // Trim and limit length
    return sanitized.trim().slice(0, maxLength);
};

/**
 * Sanitize HTML allowing specific safe tags
 * Use for: descriptions, notes with basic formatting
 * 
 * @param {string} html - HTML content to sanitize
 * @param {array} allowedTags - Tags to allow (default: basic formatting)
 * @returns {string} Sanitized HTML
 */
export const sanitizeHTML = (html, allowedTags = ['b', 'i', 'u', 'br', 'p', 'strong', 'em']) => {
    if (!html || typeof html !== 'string') return '';

    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: allowedTags,
        ALLOWED_ATTR: [] // No attributes allowed (prevents onclick, onerror, etc.)
    });
};

/**
 * Validate email format
 * 
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email format
 */
export const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return false;

    // RFC 5322 simplified regex
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Additional checks
    const minLength = 5; // a@b.c minimum
    const maxLength = 254; // RFC 5321

    return (
        regex.test(email) &&
        email.length >= minLength &&
        email.length <= maxLength &&
        !email.includes('..') && // No consecutive dots
        !email.startsWith('.') && // No leading dot
        !email.endsWith('.')      // No trailing dot
    );
};

/**
 * Sanitize and validate name input
 * 
 * @param {string} name - Name to validate
 * @param {number} minLength - Minimum length (default 2)
 * @param {number} maxLength - Maximum length (default 100)
 * @returns {object} { valid: boolean, sanitized: string, error: string|null }
 */
export const validateName = (name, minLength = 2, maxLength = 100) => {
    if (!name || typeof name !== 'string') {
        return { valid: false, sanitized: '', error: 'Nome richiesto' };
    }

    const sanitized = sanitizeText(name, maxLength);

    if (sanitized.length < minLength) {
        return {
            valid: false,
            sanitized,
            error: `Nome troppo corto (minimo ${minLength} caratteri)`
        };
    }

    if (sanitized.length > maxLength) {
        return {
            valid: false,
            sanitized: sanitized.slice(0, maxLength),
            error: `Nome troppo lungo (massimo ${maxLength} caratteri)`
        };
    }

    // Check for suspicious patterns
    if (/[<>{}]/.test(sanitized)) {
        return {
            valid: false,
            sanitized,
            error: 'Nome contiene caratteri non validi'
        };
    }

    return { valid: true, sanitized, error: null };
};

/**
 * Sanitize and validate email input
 * 
 * @param {string} email - Email to validate
 * @returns {object} { valid: boolean, sanitized: string, error: string|null }
 */
export const validateEmail = (email) => {
    if (!email || typeof email !== 'string') {
        return { valid: false, sanitized: '', error: 'Email richiesta' };
    }

    const sanitized = sanitizeText(email.toLowerCase(), 254);

    if (!isValidEmail(sanitized)) {
        return {
            valid: false,
            sanitized,
            error: 'Formato email non valido (esempio: user@domain.com)'
        };
    }

    return { valid: true, sanitized, error: null };
};

/**
 * Sanitize tags input (comma-separated)
 * 
 * @param {string} tagsString - Comma-separated tags
 * @param {number} maxTagLength - Max length per tag (default 50)
 * @param {number} maxTags - Max number of tags (default 20)
 * @returns {string} Sanitized comma-separated tags
 */
export const sanitizeTags = (tagsString, maxTagLength = 50, maxTags = 20) => {
    if (!tagsString || typeof tagsString !== 'string') return '';

    const tags = tagsString
        .split(',')
        .map(tag => sanitizeText(tag.trim(), maxTagLength))
        .filter(tag => tag.length > 0)
        .slice(0, maxTags); // Limit number of tags

    return tags.join(', ');
};

/**
 * Sanitize JSON data for safe storage
 * Recursive sanitization of object values
 * 
 * @param {object} obj - Object to sanitize
 * @param {number} depth - Current recursion depth (internal)
 * @returns {object} Sanitized object
 */
export const sanitizeObject = (obj, depth = 0) => {
    // Prevent infinite recursion
    if (depth > 10) return {};

    if (typeof obj !== 'object' || obj === null) {
        if (typeof obj === 'string') {
            return sanitizeText(obj);
        }
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => sanitizeObject(item, depth + 1));
    }

    const sanitized = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            const sanitizedKey = sanitizeText(key, 100);
            sanitized[sanitizedKey] = sanitizeObject(obj[key], depth + 1);
        }
    }

    return sanitized;
};

/**
 * Safe JSON parse with error handling
 * 
 * @param {string} jsonString - JSON string to parse
 * @param {any} defaultValue - Value to return on error (default null)
 * @returns {any} Parsed object or default value
 */
export const safeJSONParse = (jsonString, defaultValue = null) => {
    try {
        return JSON.parse(jsonString);
    } catch (error) {
        console.error('JSON parse error:', error);
        return defaultValue;
    }
};

/**
 * Remove dangerous keys (__proto__, constructor, prototype) from an object.
 * Prevents Prototype Pollution if the object is later used in a deep merge.
 * Apply this immediately after JSON.parse() on untrusted data.
 *
 * @param {any} obj - Parsed JSON object to sanitize
 * @param {number} depth - Current recursion depth (internal)
 * @returns {any} Object without dangerous keys
 */
const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

export const stripDangerousKeys = (obj, depth = 0) => {
    if (depth > 10) return obj;
    if (obj === null || typeof obj !== 'object') return obj;

    if (Array.isArray(obj)) {
        return obj.map(item => stripDangerousKeys(item, depth + 1));
    }

    const clean = {};
    for (const key of Object.keys(obj)) {
        if (DANGEROUS_KEYS.has(key)) continue;
        clean[key] = stripDangerousKeys(obj[key], depth + 1);
    }
    return clean;
};
